import { computed } from 'vue'

import { useFinder } from './useFinder'
import type { FinderSizeCriterion } from './useFinder.types'

/**
 * Encapsulates all size-card-specific logic for the Finder, keeping
 * `Finder.vue` a thin orchestrator.
 *
 * `selectedSizeOptionIds` is a two-way computed: it reads the active size
 * option ids from `useFinder`'s state, and writing to it replaces the facet
 * values for the size criterion (so the component can toggle chips via
 * `v-model`). Range-driven selection (`handleSelectSizeRange`) maps a
 * `[min, max]` window to every option whose numeric id falls within it and
 * writes back through `useFinder`, which reassigns `filtersInput` entries so
 * the existing `useProductsSearch` pipeline refetches without a page reload.
 *
 * When `finder` or `sizeCriterion` is absent (e.g. in isolation tests where the
 * Finder renders cards without selection wiring), `selectedSizeOptionIds` is
 * empty and `handleSelectSizeRange` is a no-op.
 */
export function useSizeCard(
  finder: ReturnType<typeof useFinder> | null,
  sizeCriterion: FinderSizeCriterion | undefined,
) {
  const selectedSizeOptionIds = computed<string[]>({
    get() {
      if (!finder || !sizeCriterion) return []

      return sizeCriterion.options
        .filter((option) => finder.isOptionSelected(sizeCriterion, option.id))
        .map((option) => option.id)
    },
    set(optionIds) {
      if (!finder || !sizeCriterion) return

      const values = sizeCriterion.options
        .filter((option) => optionIds.includes(option.id))
        .flatMap((option) => option.values)

      finder.setCriterionValues(sizeCriterion, values)
    },
  })

  /**
   * Range-driven size selection. Emits the committed `[min, max]` window on
   * release. Every size option whose numeric id falls within the window is
   * selected; the full extent (`[rangeMin, rangeMax]`) maps to "no filter" (an
   * empty `real_screen_size` array) so the default state shows every product.
   */
  function handleSelectSizeRange([min, max]: [number, number]): void {
    if (!finder || !sizeCriterion) return

    if (min === sizeCriterion.rangeMin && max === sizeCriterion.rangeMax) {
      finder.setCriterionValues(sizeCriterion, [])

      return
    }

    const inRangeValues = sizeCriterion.options
      .filter((option) => {
        const numericId = Number(option.id)

        return (
          Number.isFinite(numericId) && numericId >= min && numericId <= max
        )
      })
      .flatMap((option) => option.values)

    finder.setCriterionValues(sizeCriterion, inRangeValues)
  }

  return {
    selectedSizeOptionIds,
    handleSelectSizeRange,
  }
}
